import { toaster } from 'baseui/toast';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, UseFormMethods } from 'react-hook-form';
import styled from 'styled-components';
import { FinalButton } from '../../Components/FinalButton';
import { LoadingPage } from '../../Components/LoadingOverlay';
import { Text } from '../../globalStyles';
import {
  CreateSaiSurveyErrorCode,
  GetOrganizedSaiSurveysForOrgQuery,
  SaiSurveyTerm as Term,
  UpdateSaiSurveyErrorCode,
  useCreateSaiSurveyMutation,
  useGetOrganizedSaiSurveysForOrgQuery,
  useUpdateSaiSurveyMutation,
} from '../../graphQL';
import { SurveyPeriodDisplay } from './components/SurveyPeriodDisplay';
import { SurveyPeriodForm } from './components/SurveyPeriodForm';
import { SurveyProspectUploadModal } from './components/SurveyProspectUploadModal';
import { calculateYears } from './components/surveyUtils';

type SurveyPeriodFormType = {
  id?: number;
  startDate: string;
  endDate: string;
  surveyName: string;
  term: Term;
  surveyContentVersion: string;
  calendarYear: string;
  academicYearStart: string;
  academicYearEnd: string;
  targetCohortName?: string;
  numberOfStudentsInCohort?: number;
  goalResponseRate?: string;
  customContentEnabled: boolean;
  customContentTitle: string;
  customContentDescription: string;
};

const defaultSurveyPeriodValues: SurveyPeriodFormType = {
  startDate: '',
  endDate: '',
  surveyName: '',
  term: Term.Fall,
  surveyContentVersion: '',
  calendarYear: '',
  academicYearStart: '',
  academicYearEnd: '',
  targetCohortName: '',
  numberOfStudentsInCohort: undefined,
  goalResponseRate: '',
  customContentEnabled: false,
  customContentTitle: '',
  customContentDescription: '',
};

const handleOverlappingSurveyPeriodError = (
  form: UseFormMethods<SurveyPeriodFormType>,
  dateFieldsRef: React.RefObject<HTMLDivElement>,
  message: string
) => {
  form.setError('startDate', {
    type: 'invalid',
    message: message ?? 'These dates overlap with an existing survey period.',
  });
  dateFieldsRef.current?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });
  form.setError('endDate', { type: 'invalid' });
};

interface SurveyPeriodProps {
  organizationId: number;
  organizationName: string;
}

type SaiSurvey =
  GetOrganizedSaiSurveysForOrgQuery['getOrganizedSaiSurveysForOrg']['activeAndUpcoming'][number];

// Main Survey Period Config component
export function SurveyPeriod({ organizationId, organizationName }: SurveyPeriodProps): JSX.Element {
  // Replace separate booleans with a single mode state
  const [mode, setMode] = useState<'idle' | 'creating' | 'editing'>('idle');
  const [selectedSurveyPeriodId, setSelectedSurveyPeriodId] = useState<number | null>(null);
  const [stateError, setStateError] = useState<string>('');
  const [participantsSurvey, setParticipantsSurvey] = useState<SaiSurvey | null>(null);

  // Create separate form instances for create and edit
  const createForm = useForm<SurveyPeriodFormType>({
    defaultValues: defaultSurveyPeriodValues,
    mode: 'onChange',
  });

  const editForm = useForm<SurveyPeriodFormType>({
    defaultValues: defaultSurveyPeriodValues,
    mode: 'onChange',
  });

  // Refs for scrolling to date fields on overlap error
  const createDateFieldsRef = useRef<HTMLDivElement>(null);
  const editDateFieldsRef = useRef<HTMLDivElement>(null);

  // Use the appropriate form based on mode
  const currentForm = mode === 'editing' ? editForm : createForm;

  // Fetch all configs for this org
  const {
    data: organizedSurveysData,
    loading: organizedSurveysLoading,
    error: organizedSurveysError,
    refetch: refetchOrganizedSurveys,
  } = useGetOrganizedSaiSurveysForOrgQuery({
    variables: { organizationId },
    notifyOnNetworkStatusChange: true,
  });

  const [createSurveyPeriod, createMutation] = useCreateSaiSurveyMutation({
    onCompleted: ({ createSaiSurvey }) => {
      const responseType = createSaiSurvey.__typename;

      if (responseType === 'CreateSaiSurveyError') {
        if (createSaiSurvey.errorCode === CreateSaiSurveyErrorCode.OverlappingSurveyPeriod) {
          handleOverlappingSurveyPeriodError(
            createForm,
            createDateFieldsRef,
            createSaiSurvey.message
          );
          return;
        }
        setStateError(createSaiSurvey.message ?? 'Error creating survey period.');
        return;
      }

      if (responseType === 'CreateSaiSurveySuccess') {
        setMode('idle');
        setSelectedSurveyPeriodId(null);
        createForm.reset(defaultSurveyPeriodValues);
        toaster.positive('Survey period created successfully', { autoHideDuration: 3000 });
        refetchOrganizedSurveys();
        return;
      }

      setStateError(`CreateSaiSurvey: Invalid typename: ${responseType}`);
    },
    onError: error => {
      setStateError(error.message);
    },
  });
  const [updateSurveyPeriod, updateMutation] = useUpdateSaiSurveyMutation({
    onCompleted: ({ updateSaiSurvey }) => {
      const responseType = updateSaiSurvey.__typename;

      if (responseType === 'UpdateSaiSurveyError') {
        if (updateSaiSurvey.errorCode === UpdateSaiSurveyErrorCode.OverlappingSurveyPeriod) {
          handleOverlappingSurveyPeriodError(editForm, editDateFieldsRef, updateSaiSurvey.message);
          return;
        }
        setStateError(updateSaiSurvey.message ?? 'Error updating survey config.');
        return;
      }

      if (responseType === 'UpdateSaiSurveySuccess') {
        handleCancel();
        toaster.positive('Survey config updated successfully', { autoHideDuration: 3000 });
        refetchOrganizedSurveys();
        return;
      }

      setStateError(`UpdateSaiSurvey: Invalid typename: ${responseType}`);
    },
    onError: error => {
      setStateError(error.message);
    },
  });

  // Handler to enter edit mode and hydrate form
  const handleEdit = useCallback(
    (surveyPeriod: SaiSurvey) => {
      setStateError('');
      setSelectedSurveyPeriodId(surveyPeriod.id);
      editForm.reset({
        id: surveyPeriod.id,
        startDate: surveyPeriod.startDate,
        endDate: surveyPeriod.endDate,
        surveyName: surveyPeriod.name,
        term: surveyPeriod.term,
        surveyContentVersion: surveyPeriod.assessmentKey,
        calendarYear: String(surveyPeriod.calendarYear ?? ''),
        academicYearStart: String(surveyPeriod.academicYearStart ?? ''),
        academicYearEnd: String(surveyPeriod.academicYearEnd ?? ''),
        targetCohortName: surveyPeriod.targetCohortName ?? '',
        numberOfStudentsInCohort: surveyPeriod.numberOfStudentsInCohort ?? undefined,
        goalResponseRate: surveyPeriod.goalResponseRate ?? '',
        customContentEnabled: surveyPeriod.customContentEnabled ?? false,
        customContentTitle: surveyPeriod.customContentTitle ?? '',
        customContentDescription: surveyPeriod.customContentDescription ?? '',
      });
      setMode('editing');
    },
    [editForm]
  );
  const startDate = currentForm.watch('startDate');
  const term = currentForm.watch('term');

  // Calculate calendar year and academic year
  const { calendarYear, academicYearStart, academicYearEnd } = useMemo(
    () => calculateYears(startDate, term),
    [startDate, term]
  );

  const academicYearDisplay = useMemo(
    () => (academicYearStart && academicYearEnd ? `${academicYearStart}-${academicYearEnd}` : ''),
    [academicYearStart, academicYearEnd]
  );

  // Memoize handlers to prevent unnecessary re-renders
  const handleCreate = useCallback(
    async (values: SurveyPeriodFormType) => {
      setStateError('');
      await createSurveyPeriod({
        variables: {
          data: {
            startDate: values.startDate,
            endDate: values.endDate,
            organizationId: organizationId,
            name: values.surveyName,
            term: values.term,
            assessmentKey: values.surveyContentVersion,
            targetCohortName: values.targetCohortName,
            numberOfStudentsInCohort:
              values.numberOfStudentsInCohort && Number(values.numberOfStudentsInCohort) > 0
                ? Number(values.numberOfStudentsInCohort)
                : undefined,
            goalResponseRate: values.goalResponseRate,
            customContentEnabled: values.customContentEnabled,
            customContentTitle: values.customContentEnabled ? values.customContentTitle : undefined,
            customContentDescription: values.customContentEnabled
              ? values.customContentDescription
              : undefined,
          },
        },
      });
    },
    [createSurveyPeriod, organizationId]
  );

  const handleCancel = useCallback(() => {
    setMode('idle');
    setSelectedSurveyPeriodId(null);
    setStateError('');
    createForm.reset(defaultSurveyPeriodValues);
    editForm.reset(defaultSurveyPeriodValues);
  }, [createForm, editForm]);

  const handleUpdate = useCallback(
    async (values: SurveyPeriodFormType) => {
      if (!selectedSurveyPeriodId) return;
      setStateError('');
      await updateSurveyPeriod({
        variables: {
          updates: {
            id: selectedSurveyPeriodId,
            startDate: values.startDate,
            endDate: values.endDate,
            name: values.surveyName,
            term: values.term,
            assessmentKey: values.surveyContentVersion,
            targetCohortName: values.targetCohortName,
            numberOfStudentsInCohort:
              values.numberOfStudentsInCohort && Number(values.numberOfStudentsInCohort) > 0
                ? Number(values.numberOfStudentsInCohort)
                : null, // Use null to signal "clear this field"
            goalResponseRate: values.goalResponseRate,
            customContentEnabled: values.customContentEnabled,
            customContentTitle: values.customContentEnabled ? values.customContentTitle : undefined,
            customContentDescription: values.customContentEnabled
              ? values.customContentDescription
              : undefined,
          },
        },
      });
    },
    [selectedSurveyPeriodId, updateSurveyPeriod]
  );

  const handleStartCreate = useCallback(() => {
    createForm.reset(defaultSurveyPeriodValues);
    setSelectedSurveyPeriodId(null);
    setStateError('');
    setMode('creating');
  }, [createForm]);

  // Derive UI state
  const isEditing = mode === 'editing';
  const isCreating = mode === 'creating';

  const submitHandler = useMemo(
    () => currentForm.handleSubmit(isEditing ? handleUpdate : handleCreate),
    [currentForm, isEditing, handleUpdate, handleCreate]
  );

  // Render a single survey period card. A past period's roster is history, so
  // only its config stays editable.
  const renderSurveyCard = (surveyPeriod: SaiSurvey, { isPast }: { isPast: boolean }) => {
    const isEditingThis = isEditing && selectedSurveyPeriodId === surveyPeriod.id;
    return (
      <ConfigCard key={surveyPeriod.id}>
        {isEditingThis ? (
          <FormProvider {...editForm}>
            <SurveyPeriodForm
              isEditing={true}
              onSubmit={submitHandler}
              onCancel={handleCancel}
              createLoading={createMutation.loading}
              updateLoading={updateMutation.loading}
              errorMessage={stateError}
              calendarYear={calendarYear}
              academicYear={academicYearDisplay}
              dateFieldsRef={editDateFieldsRef}
            />
          </FormProvider>
        ) : (
          <>
            <SurveyPeriodDisplay surveyPeriod={surveyPeriod} />
            <CardActions>
              <FinalButton
                kind="outline_black"
                size="compact"
                onClick={() => handleEdit(surveyPeriod)}
              >
                Edit Survey Config
              </FinalButton>
              {!isPast && (
                <FinalButton
                  kind="outline_black"
                  size="compact"
                  onClick={() => setParticipantsSurvey(surveyPeriod)}
                >
                  Edit Participants
                </FinalButton>
              )}
            </CardActions>
          </>
        )}
      </ConfigCard>
    );
  };

  // Display all configs in two sections
  const renderAllConfigs = () => {
    if (organizedSurveysLoading) return <LoadingPage />;
    if (organizedSurveysError)
      return <Text.bodySmall kind="danger">Error loading configs</Text.bodySmall>;

    const organized = organizedSurveysData?.getOrganizedSaiSurveysForOrg;
    const activeAndUpcoming = organized?.activeAndUpcoming ?? [];
    const past = organized?.past ?? [];
    const activeAndUpcomingLimitReached = organized?.activeAndUpcomingLimitReached ?? false;
    const pastLimitReached = organized?.pastLimitReached ?? false;
    const limit = organized?.limit ? String(organized.limit) : '';

    return (
      <ConfigsWrapper>
        <SectionContainer>
          <ConfigsHeader>Active & Upcoming Survey Periods</ConfigsHeader>
          {activeAndUpcomingLimitReached && (
            <LimitNotice>Only showing the {limit} items ending soonest</LimitNotice>
          )}
          {activeAndUpcoming.length === 0 ? (
            <EmptyStateText>No active or upcoming survey periods.</EmptyStateText>
          ) : (
            activeAndUpcoming.map(surveyPeriod => renderSurveyCard(surveyPeriod, { isPast: false }))
          )}
        </SectionContainer>

        <SectionContainer>
          <ConfigsHeader>Past Survey Periods</ConfigsHeader>
          {pastLimitReached && <LimitNotice>Only showing {limit} most recent items.</LimitNotice>}
          {past.length === 0 ? (
            <EmptyStateText>No past survey periods.</EmptyStateText>
          ) : (
            past.map(surveyPeriod => renderSurveyCard(surveyPeriod, { isPast: true }))
          )}
        </SectionContainer>
      </ConfigsWrapper>
    );
  };

  return (
    <SectionRoot>
      <Text.h3>Survey Period Configuration</Text.h3>
      <IntroCopy>
        Configure survey periods to control when surveys are active for students.
      </IntroCopy>
      {mode === 'idle' && (
        <ButtonContainer>
          <FinalButton iconLeft="iconsWhitePlusSvg" kind="primary" onClick={handleStartCreate}>
            Add New Survey Period
          </FinalButton>
        </ButtonContainer>
      )}
      {isCreating && (
        <CreateContainer>
          <CreateTitle>Create New Survey Period Config</CreateTitle>
          <FormProvider {...createForm}>
            <SurveyPeriodForm
              isEditing={false}
              onSubmit={submitHandler}
              onCancel={handleCancel}
              createLoading={createMutation.loading}
              updateLoading={updateMutation.loading}
              errorMessage={stateError}
              calendarYear={calendarYear}
              academicYear={academicYearDisplay}
              dateFieldsRef={createDateFieldsRef}
            />
          </FormProvider>
        </CreateContainer>
      )}
      {renderAllConfigs()}
      {participantsSurvey && (
        <SurveyProspectUploadModal
          isOpen
          surveyId={participantsSurvey.id}
          surveyName={participantsSurvey.name}
          organizationName={organizationName}
          onClose={() => setParticipantsSurvey(null)}
          onUpdated={prospectCount => {
            setParticipantsSurvey(null);
            toaster.positive(
              `Participants list updated — ${prospectCount.toLocaleString()} ${
                prospectCount === 1 ? 'participant' : 'participants'
              }`,
              { autoHideDuration: 3000 }
            );
            refetchOrganizedSurveys();
          }}
        />
      )}
    </SectionRoot>
  );
}

// Styled components
const SectionRoot = styled.div`
  margin-top: 1.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e0e0e0;
`;

const IntroCopy = styled(Text.bodySmall)`
  margin: 0.5rem 0 1rem 0;
  display: block;
`;

const ButtonContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const ConfigsWrapper = styled.div`
  margin-bottom: 1.5rem;
`;

const ConfigsHeader = styled(Text.bodyBold)`
  margin: 0 0 0.5rem 0;
  display: block;
`;

const ConfigCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
  background: white;
`;

const CreateContainer = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
  padding: 1.25rem;
  background: #f8f9fa;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
`;

const CreateTitle = styled(Text.bodyBold)`
  margin: 0 0 0.5rem 0;
  display: block;
`;

const SectionContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const LimitNotice = styled(Text.bodySmall)`
  display: block;
  margin-bottom: 0.5rem;
  color: #666;
`;

const EmptyStateText = styled(Text.bodySmall)`
  font-style: italic;
  display: block;
  margin-bottom: 0.5rem;
`;

const CardActions = styled.div`
  margin-top: 0.75rem;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;
